import { clearSession } from './session';
import api from '../actions/api';
import { reloadPage } from './browserLocation';
import { applySessionHeaders } from './requestConfig';

// The logout request itself must be exempt from the 401 handling below, or its own 401 starts
// another logout, and so on for as long as the server keeps answering 401. The marker rides on the
// axios request config rather than on a second axios instance, so the logout request still goes
// through the request interceptor and keeps its Authorization header.
const LOGOUT_REQUEST_CONFIG = { skipSessionHandling: true };

// One logout at a time: several requests expiring together used to fire one logout request each
// (I-2). A logout that never answers leaves the flag latched — that is not the user-visible
// problem (the session is not cleared either way) and is tracked in the backlog rather than
// papered over with a timer.
let logoutInFlight = false;

// Both outcomes of the logout request end the same way: the session cleared locally (shared with
// every other logout, utils/session.js), then a reload.
const endSession = () => {
  logoutInFlight = false;
  clearSession();
  reloadPage();
};

const handleLogOut = () => {
  if (logoutInFlight) {
    return;
  }
  logoutInFlight = true;
  // Both handlers run the same cleanup: the server's answer must never gate the local logout, or a
  // server that refuses to end the session keeps the browser signed in with an expired token.
  api.post('auth/logout', null, LOGOUT_REQUEST_CONFIG).then(endSession, endSession)
}

export default {
  setupInterceptors: (store) => {
    // Called once in production (src/index.js) and once per test; a fresh registration means a
    // fresh in-flight flag, which `endSession` alone cannot guarantee when a logout never
    // settles.
    logoutInFlight = false;

    api.interceptors.request.use(
      config => {
        applySessionHeaders(config.headers);
        try {
          config.headers['X-Client-Timezone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
        } catch (e) {}
        return config;
      }
    );

    // Add a response interceptor
    api.interceptors.response.use(function (response) {
        return response;
    }, function (err) {
      const error = err.response;
      // The logout request opts out of the 401 branch only; a 409 from it still has to reach the
      // store.
      const skip = !!(err.config && err.config.skipSessionHandling);
      // if error is 401 
      if (error && error.status===401 && !skip) {
        // TODO Need to check why we are reloading page after invalid token
        // Guard the read: a 401 can arrive before any end-page-url was stored, and
        // `null.split(...)` would throw a TypeError out of the interceptor instead of
        // logging out (I-1).
        const url_segment = (localStorage.getItem("end-page-url") || "").split("/")
        if (!url_segment.includes("finished")){
          // The pathname only: the response carries the request config, its Authorization header
          // and body included, and Sentry keeps console lines as breadcrumbs (SE-LOG-AXIOS-DANE).
          console.log("[Token expired] logging out... ", window.location.pathname)
          handleLogOut();
        }
      } else if(error && error.status===409) {
        store.dispatch({ type: 'NEW_APP_VERSION', payload: error.data })
      }
      // A network failure or a timeout has no response: reject with the axios error itself, so the
      // *_ERROR action carries a payload and the caller learns what failed (an undefined reason
      // threw out of every error branch that read the payload's body, and left its spinner on).
      return Promise.reject(error || err);
    });

  }
}
